import React, { useEffect, useRef, useState } from "react";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { IProduct, IProductCategory } from "../../../models/catalogue";
import { IProductID, IWebPageURL } from "../../../models/nominals";
import { BreakPoint } from "../../../models/screen";
import { check } from "../../../models/utils";
import { trackAddToBasketEvent } from "../../../utils/analytics";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { navigateToURL } from "../../../utils/urls";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { addProductToBasket } from "../actions";
import { StickyConfigurator as Component } from "../components/StickyConfigurator";
import { defaults } from "../defaults";
import { getSelectedUpgradeIDFromURL } from "../history";
import { loadUserConfigurableBundles } from "../loaders";
import {
    IPLCProductCategorySelector,
    IPLCProductCategorySelectorOption,
    IUpsellModalComponentFn,
    IUpsellModalComponentRef,
    PLCProductCategorySelectors,
} from "../models";
import {
    setAddToBasketBtnState,
    setAddToBasketErrorModalState,
} from "../reducers";
import {
    baseVariantSelector,
    rootProductSelector,
    selectedCategoriesSelector,
    upgradedVariantPriceSelector,
    upgradedVariantSelector,
} from "../selectors";
import { loadConcreteBundles, loadPrice } from "../thunks";
import { isPreorderProduct } from "../utils";

// Helper functions extracted outside the component
const parseCategorySelectors = (
    categorySelectorsJSON: string,
): readonly IPLCProductCategorySelector[] => {
    const selectors: readonly IPLCProductCategorySelector[] = check(
        PLCProductCategorySelectors.decode(JSON.parse(categorySelectorsJSON)),
    );
    return selectors;
};

const getStickyThreshold = (stickyTrigger: HTMLElement | null): number => {
    if (!stickyTrigger) {
        return 0;
    }
    // Use "pdp-hero__content" for stickyTrigger
    // That would be always present in the DOM
    // Take its bottom for triggering visibility
    const document = getDocument();
    if (!document) {
        return 0;
    }
    return (
        stickyTrigger.getBoundingClientRect().bottom -
        document.body.getBoundingClientRect().top +
        stickyTrigger.offsetHeight
    );
};

const isMobileWidth = (): boolean => {
    return getViewportBreakpoint() <= BreakPoint.MEDIUM;
};

const getScrollTop = (): number => {
    const window = getWindow();
    const document = getDocument();
    if (!window || !document) {
        return 0;
    }
    return (
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop
    );
};

const getScrollHeight = (): number => {
    const document = getDocument();
    if (!document) {
        return 0;
    }
    return document.documentElement.scrollHeight || document.body.scrollHeight;
};

export const StickyConfigurator = (props: {
    // Site Page URLs for links and redirects
    financingLink?: IWebPageURL;
    basketLink: IWebPageURL;
    configureGiftsLink: IWebPageURL;

    // Optional: Up Sell Modal functionality
    showUpsellModal?: (product: IProduct | null) => boolean;
    getUpsellModalComponentFn?: (
        product: IProduct,
    ) => IUpsellModalComponentFn | null;
    upsellModal?: IUpsellModalComponentFn;

    // Callback Hooks
    onBaseVariantChanged?: (baseVariant: IProduct | null) => void;
    onEditClick?: () => void;

    // Pricing UI options
    strikeThroughMSRP?: boolean;
    actualPriceStyle?: string;
    overrideFinancingCopy?: string;

    // Threshold for sticky appearance
    stickyTrigger?: HTMLElement | null;
    isPLCVersion: boolean;

    categorySelectors?: string;
}): React.ReactNode => {
    // Redux state
    const configuratorState = useAppSelector(
        (rootState) => rootState.configurator || defaults,
    );
    const rootProduct = useAppSelector((rootState) =>
        rootProductSelector(rootState.configurator || defaults),
    );
    const baseVariant = useAppSelector((rootState) =>
        baseVariantSelector(rootState.configurator || defaults),
    );
    const upgradedVariant = useAppSelector((rootState) =>
        upgradedVariantSelector(rootState.configurator || defaults),
    );
    const price = useAppSelector((rootState) =>
        upgradedVariantPriceSelector(rootState.configurator || defaults),
    );
    const selectedCategoriesFromState = useAppSelector((rootState) =>
        selectedCategoriesSelector(rootState.configurator || defaults),
    );
    const concreteBundles = configuratorState.entities.concreteBundles;
    const optionValues = configuratorState.ui.optionValues;
    const quantity = configuratorState.ui.quantity;
    const addToBasketCooldownActive =
        configuratorState.ui.addToBasketCooldownActive;
    const addToBasketButtonText = configuratorState.ui.addToBasketButtonText;
    const addToBasketErrorOpen = configuratorState.ui.addToBasketErrorOpen;
    const addToBasketErrorReason = configuratorState.ui.addToBasketErrorReason;

    // Dispatchers and loaders
    const dispatch = useAppDispatch();

    // Local state
    const [isVisible, setIsVisible] = useState(false);
    const [stickyElemHeight, setStickyElemHeight] = useState(
        getDocument()?.documentElement.offsetHeight ?? 0,
    );

    // Refs
    const upsellModalRef = useRef<IUpsellModalComponentRef>(null);
    const baseVariantIdRef = useRef<IProductID | null>(baseVariant?.id ?? null);
    const upgradedVariantIdRef = useRef<IProductID | null>(
        upgradedVariant?.id ?? null,
    );
    const quantityRef = useRef<number>(quantity);

    // Parse category selectors
    const categorySelectors = !props.categorySelectors
        ? []
        : parseCategorySelectors(props.categorySelectors);

    // Compute selected categories
    const getSelectedCategories = (): IProductCategory[] => {
        // Load selected categories (if they exist)
        if (Object.keys(selectedCategoriesFromState).length === 0) {
            return [];
        }

        const selectedCategoryObject: IProductCategory[] = [];

        // Match the currently selected categories from the configurator to their values
        categorySelectors.forEach((obj: IPLCProductCategorySelector) => {
            const selectedCategoryOption:
                | IPLCProductCategorySelectorOption
                | undefined = obj.value.options.find(
                (elem: IPLCProductCategorySelectorOption) => {
                    return (
                        elem.category.id === selectedCategoriesFromState[obj.id]
                    );
                },
            );

            if (selectedCategoryOption?.category) {
                selectedCategoryObject.push({
                    name: obj.value.name,
                    value: selectedCategoryOption.category.name,
                });
            }
        });

        return selectedCategoryObject;
    };
    const selectedCategories = getSelectedCategories();

    // Update add to basket text based on preorder status
    const updateAddToBasketText = () => {
        // Change add to cart label to preorder where necessary
        const isPreorder =
            upgradedVariant && isPreorderProduct(upgradedVariant);
        const newAddToBasketButtonText = isPreorder
            ? t`Pre-Order`
            : t`Add to Cart`;
        if (addToBasketButtonText !== newAddToBasketButtonText) {
            dispatch(
                setAddToBasketBtnState({
                    cooldownActive: false,
                    text: newAddToBasketButtonText,
                }),
            );
        }
    };

    // Redirect to next page helper
    const redirectToNextPage = async () => {
        // Check if the user has any gifts to configure
        const bundles = await loadUserConfigurableBundles();
        const nextURL =
            bundles.length <= 0 ? props.basketLink : props.configureGiftsLink;
        setTimeout(() => {
            navigateToURL(nextURL);
        }, 0);
    };

    // Scroll handler
    const onScroll = () => {
        if (!baseVariant) {
            setIsVisible(false);
        }

        const top = getScrollTop();
        const stickyExit = getScrollHeight();
        const stickyEnter = getStickyThreshold(props.stickyTrigger || null);
        if (top >= stickyEnter && top <= stickyExit) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    };

    // Edit click handler
    const onEditClick = () => {
        if (props.onEditClick) {
            props.onEditClick();
        }
    };

    // Add to basket handler
    const onAddToBasket = async () => {
        if (!upgradedVariant) {
            return;
        }

        // Before add to basket: Disable buttons
        dispatch(
            setAddToBasketBtnState({ cooldownActive: true, text: t`Adding` }),
        );

        try {
            await addProductToBasket(upgradedVariant.url, quantity);

            // After add to basket: Re-enable buttons
            dispatch(
                setAddToBasketBtnState({
                    cooldownActive: false,
                    text: t`Added`,
                }),
            );

            // Send an `add_to_cart` event to the data layer
            trackAddToBasketEvent(upgradedVariant, quantity);

            // Either show the upsell modal or redirect customer to the basket page
            if (
                upsellModalRef.current &&
                concreteBundles.length > 0 &&
                props.showUpsellModal &&
                props.showUpsellModal(upgradedVariant)
            ) {
                upsellModalRef.current.openModal();
            } else {
                void redirectToNextPage();
            }
        } catch (e) {
            // On add to basket error: Display the error to the user
            let reason = t`An unexpected error occurred. Please try again.`;
            try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                reason = e.response.body.reason;
            } catch (e2) {
                console.error(e2);
            }

            // Re-enable buttons
            const isPreorder =
                upgradedVariant && isPreorderProduct(upgradedVariant);
            const newAddToBasketButtonText = isPreorder
                ? t`Pre-Order`
                : t`Add to Cart`;
            dispatch(
                setAddToBasketBtnState({
                    cooldownActive: false,
                    text: newAddToBasketButtonText,
                }),
            );
            dispatch(setAddToBasketErrorModalState({ isOpen: true, reason }));
        }
    };

    // Close error modal handler
    const onCloseErrorModal = () => {
        dispatch(setAddToBasketErrorModalState({ isOpen: false, reason: "" }));
    };

    // Set sticky configurator height handler
    const handleSetStickyConfiguratorHeight = (height: number) => {
        setStickyElemHeight(height);
    };

    // Upsell proceed handler
    const onUpsellProceed = () => {
        void redirectToNextPage();
    };

    // Build upsell modal
    const buildUpsellModal = () => {
        if (!props.getUpsellModalComponentFn || !upgradedVariant) {
            return null;
        }
        const UpsellModal = props.getUpsellModalComponentFn(upgradedVariant);
        if (!UpsellModal) {
            return null;
        }
        return (
            <UpsellModal
                ref={upsellModalRef}
                bundles={concreteBundles}
                onProceed={onUpsellProceed}
            />
        );
    };

    // Mount effect: add scroll listener
    useEffect(() => {
        const document = getDocument();
        if (!document) {
            return;
        }
        document.addEventListener("scroll", onScroll);
        return () => {
            document.removeEventListener("scroll", onScroll);
        };
    }, []);

    // Effect: handle base variant changes
    useEffect(() => {
        const prevBaseVariantID = baseVariantIdRef.current;
        const nextBaseVariantID = baseVariant?.id ?? null;

        if (prevBaseVariantID !== nextBaseVariantID) {
            // Load bundle data for the new variant
            if (nextBaseVariantID) {
                const selectedUpgradeID = getSelectedUpgradeIDFromURL();
                dispatch(
                    loadConcreteBundles({
                        variantID: nextBaseVariantID,
                        selectedUpgradeID: selectedUpgradeID,
                    }),
                );
            }

            // Trigger the onBaseVariantChanged callback
            if (props.onBaseVariantChanged) {
                props.onBaseVariantChanged(baseVariant);
            }

            baseVariantIdRef.current = nextBaseVariantID;
        }
    }, [baseVariant, dispatch, props]);

    // Effect: handle upgraded variant and quantity changes
    useEffect(() => {
        const prevUpgradedVariantID = upgradedVariantIdRef.current;
        const nextUpgradedVariantID = upgradedVariant?.id ?? null;
        const upgradedVariantChanged =
            prevUpgradedVariantID !== nextUpgradedVariantID;

        const prevQty = quantityRef.current;
        const nextQty = quantity;
        const qtyChanged = prevQty !== nextQty;

        if (
            nextUpgradedVariantID &&
            (upgradedVariantChanged || qtyChanged) &&
            quantity !== 1
        ) {
            // Load price data for the new variant
            void dispatch(
                loadPrice({ productID: nextUpgradedVariantID, quantity }),
            );
        }

        if (upgradedVariantChanged && upgradedVariant) {
            updateAddToBasketText();
        }

        upgradedVariantIdRef.current = nextUpgradedVariantID;
        quantityRef.current = nextQty;
    }, [upgradedVariant, quantity, dispatch]);

    // Render logic
    if (!rootProduct) {
        return null;
    }

    if (!props.isPLCVersion) {
        if (!isMobileWidth()) {
            return null;
        }
    }

    return (
        <Component
            isPLCVersion={props.isPLCVersion}
            financingLink={props.financingLink || null}
            rootProduct={rootProduct}
            baseVariant={baseVariant}
            upgradedVariant={upgradedVariant}
            price={price}
            concreteBundles={concreteBundles}
            optionValues={optionValues}
            quantity={quantity}
            addToBasketCooldownActive={addToBasketCooldownActive}
            addToBasketButtonText={addToBasketButtonText}
            onAddToBasket={onAddToBasket}
            addToBasketErrorOpen={addToBasketErrorOpen}
            addToBasketErrorReason={addToBasketErrorReason}
            onCloseErrorModal={onCloseErrorModal}
            setStickyConfiguratorHeight={handleSetStickyConfiguratorHeight}
            stickyElemHeight={stickyElemHeight}
            isVisible={isVisible}
            onEditClick={onEditClick}
            strikeThroughMSRP={props.strikeThroughMSRP || false}
            actualPriceStyle={props.actualPriceStyle || ""}
            overrideFinancingCopy={props.overrideFinancingCopy || ""}
            selectedCategories={selectedCategories}
        >
            {buildUpsellModal()}
        </Component>
    );
};
